import { isFunction } from '@/global/utils/utils'
import { computed } from 'vue'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import ColorCell from '@/global/components/table/components/ColorCell.vue'

const { buildConfig } = useTableService()

const UI_VIEW = 'road_maintenance_tags'
const TAB_KEY = 'tags'
const API_CONFIG = {
  get: {
    module: 'road-maintenance',
    route: 'tags'
  }
}

export function useTable (options) {
  const {
    trans,
    closeInfoDialogFn,
    openInfoDialogFn,
    setInfoDialogLoaderFn,
    openTagDialogFn
  } = options || {}

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('road-maintenance.tags'),
        bolded: true
      }
    ]
  }))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/tags.name'),
      key: 'description',
      always_visible: true
    },
    {
      label: trans('road-maintenance/tags.color'),
      key: 'color',
      component: ColorCell,
      always_visible: true
    }
  ]))

  // A tag's description is kept as the translation key it is - the shared codebooks
  // behind these come over that way - so the listing resolves it the same way every
  // other screen in the module does. A tag written by hand has no translation of its
  // own and `trans` hands its text back unchanged.
  const cellsConfig = computed(() => ({
    description: {
      custom_display_value: (row) => trans(row?.description)
    }
  }))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans('road-maintenance/tags.name')
      }
    ]
  }))

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TAB_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const deleteTagHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      setInfoDialogLoaderFn(true)
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`incoming-requests/traffic-regulation/${id}`)
      fetchTableData()
      closeInfoDialogFn()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onTagDelete = (event) => {
    const { description } = event?.data || {}
    const name = description ? trans(description) : ''
    const nameTitle = name.length > 20
      ? name.substring(0, 20) + '...'
      : name

    openInfoDialogFn({
      title: trans('road-maintenance/tags.delete_title') + (nameTitle ? ' - ' + nameTitle : ''),
      message: trans('road-maintenance/tags.delete_message'),
      onConfirm: () => deleteTagHandler(event)
    })
  }

  const onShowDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    openTagDialogFn({
      title: trans('road-maintenance/tags.update_title'),
      editId: id
    })
  }

  const onCreateTag = () => {
    openTagDialogFn({
      title: trans('road-maintenance/tags.create_title')
    })
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    deleteAction(trans, onTagDelete)
  ]))

  const config = computed(() => {
    return {
      tableConfig: buildConfig({
        uiView: UI_VIEW,
        key: TAB_KEY,
        apiConfig: API_CONFIG,
        filters_and_columns_visibility_config: {
          showNewBtn: true,
          newBtnPosition: 'start',
          newClick: onCreateTag
        },
        rowDblClick: onShowDetails,
        tableConfig: {
          headers: headers.value,
          cellsConfig: cellsConfig.value,
          actionsConfig: actionsConfig.value,
          filtersConfig: filtersConfig.value
        }
      }),
      breadcrumbs: breadcrumbs.value
    }
  })

  return {
    config,
    fetchTableData
  }
}
